// @flow strict

/* eslint-disable css-modules/no-unused-class */
import * as React from 'react';

import { v4 as uuid } from 'uuid';
import debounce from 'lodash.debounce';

import styles from './style.scss';
import { Item } from './Internal/Item';
import { BackNext, Variants as BNVariants } from './Internal/BackNext';
import { Ellipsis } from './Internal/Ellipsis';

type PaginationPropsType = {|
  total: number,
  selected: number,
  urlTemplate: string,
|};

function range(from: number, to: number): Array<number> {
  return Array.from({ length: to - from }, (v, i) => i + from);
}

const addEventListener = (target: EventTarget, type: string, listener: any) => {
  target.addEventListener(type, listener);
  return () => {
    target.removeEventListener(type, listener);
  };
};

const truncateItems = (
  pages: number[],
  centerItems: number,
  selectedPage: number,
  offset: number,
): number[] => {
  const centerItemsOffset = centerItems - offset;
  const selectedIndex: number = pages.indexOf(selectedPage);
  const halfCenterItems: number = Math.ceil(centerItemsOffset / 2);
  const headCutoff: number = halfCenterItems;
  const tailCutoff: number = pages.length - halfCenterItems - 1;

  // Here we generate the arrays of page numbers required,
  // using -1 as placeholders for ellipses.
  if (selectedIndex <= headCutoff) {
    return pages.slice(1, centerItemsOffset).concat(-1);
  }
  if (selectedIndex >= tailCutoff) {
    return [-1, ...pages.slice(-centerItemsOffset, -1)];
  }
  const remaining = pages.slice(
    selectedIndex - halfCenterItems + offset,
    selectedIndex + halfCenterItems - 1,
  );
  return [-1, ...remaining.concat(-1)];
};

const getAllowTotalPagesDisplayed = (wrapperElement: HTMLElement, maxDisplayed: number) => {
  const itemSize = 42;

  if (wrapperElement.clientWidth > itemSize * maxDisplayed + 2) {
    return maxDisplayed;
  }

  if (wrapperElement.clientWidth > itemSize * 7 + 2) {
    return 7;
  }

  return 5;
};

const centerItems = (total: number, selected: number, wrapperElement: ?HTMLElement): number[] => {
  if (total === 1) {
    return [1];
  }
  const totalPagesDisplayed = wrapperElement ? getAllowTotalPagesDisplayed(wrapperElement, 11) : 11;
  const centeredPages =
    total <= totalPagesDisplayed
      ? range(2, total)
      : truncateItems(range(1, total + 1), totalPagesDisplayed, selected, 2);
  return [1, ...centeredPages, total];
};

const previous = (n: number): number => (n === 1 ? 1 : n - 1);
const next = (n: number, total: number): number => (n === total ? total : n + 1);
const buildUrl = (template: string, page: number): string =>
  // eslint-disable-next-line no-template-curly-in-string
  template.replace('${page}', page.toString());

class Pagination extends React.Component<$Exact<PaginationPropsType>, {}> {
  _wrapperNode: ?HTMLElement;

  _unbindHandlers: Array<() => void> | void;

  _debouncedWindowResize: Function;

  constructor() {
    super();
    this._debouncedWindowResize = debounce(this._requestUpdateDisplayedItems, 100);
  }

  _requestUpdateDisplayedItems = () => this.forceUpdate();

  _handleEvents = () => {
    this._unbindHandlers = [addEventListener(window, 'resize', this._windowResize)];
  };

  _unBindEvents = () => {
    if (this._unbindHandlers !== undefined) {
      this._unbindHandlers.forEach(func => func());
      this._unbindHandlers = undefined;
    }
  };

  _windowResize = () => {
    this._debouncedWindowResize();
  };

  _updateDisplayedItemsNow = () => window.requestAnimationFrame(this._requestUpdateDisplayedItems);

  componentDidMount() {
    this._handleEvents();
    this._debouncedWindowResize();
  }

  componentWillUnmount() {
    this._unBindEvents();
    this._debouncedWindowResize.cancel();
  }

  render() {
    const { selected, total = 0, urlTemplate } = this.props;

    if (total < 1) {
      return null;
    }

    return (
      <div
        ref={el => {
          this._wrapperNode = el;
        }}
        className={styles.wrapper}
      >
        <ul role="navigation" aria-label="Pagination" className={styles.pagination}>
          <BackNext
            variant={BNVariants.Back}
            disabled={selected === 1}
            href={buildUrl(urlTemplate, previous(selected))}
          />
          {centerItems(total, selected, this._wrapperNode).map(
            (index: number): React.Element<typeof Ellipsis> | React.Element<typeof Item> => {
              if (index === -1) {
                return <Ellipsis key={`${uuid()}`} />;
              }

              return (
                <Item
                  key={`${uuid()}`}
                  displayId={`${index}`}
                  selected={selected === index}
                  href={buildUrl(urlTemplate, index)}
                />
              );
            },
          )}
          <BackNext
            variant={BNVariants.Next}
            disabled={selected === total}
            href={buildUrl(urlTemplate, next(selected, total))}
          />
        </ul>
      </div>
    );
  }
}

export default Pagination;
